import { useState, useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChevronRight, ChevronDown, Table2, Eye, Braces, Search,
} from 'lucide-react';
import { apiClient } from '@/api/client';
import { useMobileStore } from '@/stores/mobile-store';
import { useWorkspaceStore } from '@/stores/workspace-store';
import { useTapHandler } from '@/hooks/useTapHandler';
import { MobileActionSheet } from '../MobileActionSheet';
import type { SchemaMetadata, SchemaMeta } from '@/types';

interface SchemaBrowserScreenProps {
  params: Record<string, unknown>;
}

export function SchemaBrowserScreen({ params }: SchemaBrowserScreenProps) {
  const connectionId = params.connectionId as string;
  const connectionName = (params.connectionName as string) ?? 'Database';
  const pushScreen = useMobileStore((s) => s.pushScreen);
  const openTab = useWorkspaceStore((s) => s.openTab);

  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [searchText, setSearchText] = useState('');
  const [actionSheet, setActionSheet] = useState<{
    items: { label: string; icon?: typeof Table2; onClick: () => void; danger?: boolean }[];
  } | null>(null);

  const { data: schema, isLoading, error } = useQuery<SchemaMetadata>({
    queryKey: ['schema', connectionId],
    queryFn: () => apiClient.get<SchemaMetadata>(`/api/schema/${connectionId}`),
    enabled: !!connectionId,
    staleTime: 60_000,
  });

  const toggle = useCallback((key: string) => {
    setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleTableTap = useCallback(
    (schemaName: string, tableName: string) => {
      // Open tab on desktop for cross-device sync
      openTab({
        type: 'table',
        id: `table:${connectionId}:${schemaName}.${tableName}`,
        connectionId,
        schema: schemaName,
        table: tableName,
        where: '',
        orderBy: '',
      });

      // Navigate to data view on mobile
      pushScreen({
        type: 'data-view',
        title: tableName,
        params: { connectionId, schema: schemaName, table: tableName },
      });
    },
    [connectionId, openTab, pushScreen],
  );

  const handleViewTap = useCallback(
    (schemaName: string, viewName: string) => {
      openTab({
        type: 'table',
        id: `table:${connectionId}:${schemaName}.${viewName}`,
        connectionId,
        schema: schemaName,
        table: viewName,
        where: '',
        orderBy: '',
      });

      pushScreen({
        type: 'data-view',
        title: viewName,
        params: { connectionId, schema: schemaName, table: viewName },
      });
    },
    [connectionId, openTab, pushScreen],
  );

  const handleTableLongPress = useCallback(
    (schemaName: string, tableName: string) => {
      setActionSheet({
        items: [
          {
            label: 'View Data',
            icon: Table2,
            onClick: () => handleTableTap(schemaName, tableName),
          },
          {
            label: 'View Structure',
            icon: Braces,
            onClick: () => {
              openTab({
                type: 'structure',
                id: `structure:${connectionId}:${schemaName}.${tableName}`,
                connectionId,
                schema: schemaName,
                table: tableName,
              });
              pushScreen({
                type: 'schema-browser',
                title: `${tableName} (structure)`,
                params: { connectionId, connectionName },
              });
            },
          },
          {
            label: 'New Query',
            icon: Search,
            onClick: () => {
              openTab({
                type: 'query',
                id: `query:${connectionId}:${Date.now()}`,
                connectionId,
                title: `Query ${tableName}`,
                sql: `SELECT * FROM ${schemaName}.${tableName} LIMIT 100;`,
              });
            },
          },
        ],
      });
    },
    [connectionId, connectionName, openTab, pushScreen, handleTableTap],
  );

  const filter = searchText.toLowerCase().trim();

  const filteredDatabases = useMemo(() => {
    if (!schema) return [];
    if (!filter) return schema.databases;

    return schema.databases
      .map((db) => ({
        ...db,
        schemas: db.schemas
          .map((s) => ({
            ...s,
            tables: s.tables.filter((t) => t.name.toLowerCase().includes(filter)),
            views: s.views.filter((v) => v.name.toLowerCase().includes(filter)),
            functions: s.functions.filter((f) => f.name.toLowerCase().includes(filter)),
          }))
          .filter((s) => s.tables.length > 0 || s.views.length > 0 || s.functions.length > 0),
      }))
      .filter((db) => db.schemas.length > 0);
  }, [schema, filter]);

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        {[1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="rounded animate-pulse"
            style={{ height: 48, backgroundColor: 'var(--dbgo-panel)' }}
          />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 text-center">
        <div className="text-accent-red text-sm">
          {error instanceof Error ? error.message : 'Failed to load schema'}
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      {/* Search box */}
      <div className="px-4 pt-3 pb-2 shrink-0">
        <div
          className="flex items-center gap-2 rounded-lg px-3"
          style={{ height: 40, backgroundColor: 'var(--dbgo-input)' }}
        >
          <Search size={16} className="text-text-muted shrink-0" />
          <input
            type="text"
            placeholder="Filter objects..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="flex-1 bg-transparent text-sm text-text-primary outline-none placeholder:text-text-muted"
          />
        </div>
      </div>

      {/* Schema tree */}
      <div className="flex-1 overflow-y-auto px-2 pb-4">
        {filteredDatabases.length === 0 && filter ? (
          <div className="text-center py-8 text-text-muted text-sm">
            No matching objects
          </div>
        ) : (
          filteredDatabases.map((db) => (
            <DatabaseNode
              key={db.name}
              dbName={db.name}
              schemas={db.schemas}
              connectionName={connectionName}
              expanded={expanded}
              toggle={toggle}
              onTableTap={handleTableTap}
              onViewTap={handleViewTap}
              onTableLongPress={handleTableLongPress}
              autoExpand={filteredDatabases.length === 1}
            />
          ))
        )}
      </div>

      {actionSheet && (
        <MobileActionSheet
          items={actionSheet.items}
          onClose={() => setActionSheet(null)}
        />
      )}
    </div>
  );
}

// ─── Database Node ──────────────────────────────────────

interface DatabaseNodeProps {
  dbName: string;
  schemas: SchemaMeta[];
  connectionName: string;
  expanded: Record<string, boolean>;
  toggle: (key: string) => void;
  onTableTap: (schema: string, table: string) => void;
  onViewTap: (schema: string, view: string) => void;
  onTableLongPress: (schema: string, table: string) => void;
  autoExpand: boolean;
}

function DatabaseNode({
  dbName,
  schemas,
  expanded,
  toggle,
  onTableTap,
  onViewTap,
  onTableLongPress,
  autoExpand,
}: DatabaseNodeProps) {
  const dbKey = `db:${dbName}`;
  const isExpanded = expanded[dbKey] ?? autoExpand;

  return (
    <div>
      <button
        onClick={() => toggle(dbKey)}
        className="w-full flex items-center gap-2 px-2 text-left active:bg-dbgo-tab-hover rounded"
        style={{ height: 48 }}
      >
        {isExpanded ? (
          <ChevronDown size={16} className="text-text-muted shrink-0" />
        ) : (
          <ChevronRight size={16} className="text-text-muted shrink-0" />
        )}
        <span className="text-sm font-medium text-text-primary truncate">{dbName}</span>
        <span className="text-xs text-text-muted ml-auto shrink-0">
          {schemas.reduce((n, s) => n + s.tables.length, 0)} tables
        </span>
      </button>

      {isExpanded &&
        schemas.map((s) => (
          <SchemaNode
            key={s.name}
            schema={s}
            expanded={expanded}
            toggle={toggle}
            onTableTap={onTableTap}
            onViewTap={onViewTap}
            onTableLongPress={onTableLongPress}
            autoExpand={schemas.length === 1}
          />
        ))}
    </div>
  );
}

// ─── Schema Node ──────────────────────────────────────

interface SchemaNodeProps {
  schema: SchemaMeta;
  expanded: Record<string, boolean>;
  toggle: (key: string) => void;
  onTableTap: (schema: string, table: string) => void;
  onViewTap: (schema: string, view: string) => void;
  onTableLongPress: (schema: string, table: string) => void;
  autoExpand: boolean;
}

function SchemaNode({
  schema: s,
  expanded,
  toggle,
  onTableTap,
  onViewTap,
  onTableLongPress,
  autoExpand,
}: SchemaNodeProps) {
  const tablesKey = `schema:${s.name}:tables`;
  const viewsKey = `schema:${s.name}:views`;
  const funcsKey = `schema:${s.name}:functions`;

  const tablesExpanded = expanded[tablesKey] ?? autoExpand;
  const viewsExpanded = expanded[viewsKey] ?? false;
  const funcsExpanded = expanded[funcsKey] ?? false;

  return (
    <div className="pl-4">
      {/* Tables folder */}
      {s.tables.length > 0 && (
        <div>
          <button
            onClick={() => toggle(tablesKey)}
            className="w-full flex items-center gap-2 px-2 text-left active:bg-dbgo-tab-hover rounded"
            style={{ height: 44 }}
          >
            {tablesExpanded ? (
              <ChevronDown size={14} className="text-text-muted shrink-0" />
            ) : (
              <ChevronRight size={14} className="text-text-muted shrink-0" />
            )}
            <Table2 size={14} className="text-accent-blue shrink-0" />
            <span className="text-sm text-text-secondary">Tables</span>
            <span className="text-xs text-text-muted ml-auto">({s.tables.length})</span>
          </button>

          {tablesExpanded && (
            <div className="pl-6">
              {s.tables.map((t) => (
                <TableRow
                  key={t.name}
                  name={t.name}
                  onTap={() => onTableTap(s.name, t.name)}
                  onLongPress={() => onTableLongPress(s.name, t.name)}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Views folder */}
      {s.views.length > 0 && (
        <div>
          <button
            onClick={() => toggle(viewsKey)}
            className="w-full flex items-center gap-2 px-2 text-left active:bg-dbgo-tab-hover rounded"
            style={{ height: 44 }}
          >
            {viewsExpanded ? (
              <ChevronDown size={14} className="text-text-muted shrink-0" />
            ) : (
              <ChevronRight size={14} className="text-text-muted shrink-0" />
            )}
            <Eye size={14} className="text-accent-purple shrink-0" />
            <span className="text-sm text-text-secondary">Views</span>
            <span className="text-xs text-text-muted ml-auto">({s.views.length})</span>
          </button>

          {viewsExpanded && (
            <div className="pl-6">
              {s.views.map((v) => (
                <button
                  key={v.name}
                  onClick={() => onViewTap(s.name, v.name)}
                  className="w-full text-left px-2 text-sm text-text-primary truncate active:bg-dbgo-tab-hover rounded"
                  style={{ height: 44 }}
                >
                  {v.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Functions folder */}
      {s.functions.length > 0 && (
        <div>
          <button
            onClick={() => toggle(funcsKey)}
            className="w-full flex items-center gap-2 px-2 text-left active:bg-dbgo-tab-hover rounded"
            style={{ height: 44 }}
          >
            {funcsExpanded ? (
              <ChevronDown size={14} className="text-text-muted shrink-0" />
            ) : (
              <ChevronRight size={14} className="text-text-muted shrink-0" />
            )}
            <Braces size={14} className="text-accent-yellow shrink-0" />
            <span className="text-sm text-text-secondary">Functions</span>
            <span className="text-xs text-text-muted ml-auto">({s.functions.length})</span>
          </button>

          {funcsExpanded && (
            <div className="pl-6">
              {s.functions.map((f) => (
                <button
                  key={f.name}
                  className="w-full text-left px-2 text-sm text-text-primary truncate active:bg-dbgo-tab-hover rounded"
                  style={{ height: 44 }}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Table Row with long-press ──────────────────────────

function TableRow({
  name,
  onTap,
  onLongPress,
}: {
  name: string;
  onTap: () => void;
  onLongPress: () => void;
}) {
  const tapHandlers = useTapHandler({ onTap, onLongPress });

  return (
    <button
      className="w-full text-left px-2 text-sm text-text-primary truncate active:bg-dbgo-tab-hover rounded"
      style={{ height: 44 }}
      {...tapHandlers}
    >
      {name}
    </button>
  );
}
